import { IconType } from '../../../@types/icons';
import { Claim, TenantCapabilities } from '../../../@types/organization';
import { ITranslator } from 'translation/generated/translationInterface';
import { getClaimsByTemplateRole } from 'utils/userAccessTemplates';
import {
  INTERNAL_PERMISSION_CLAIMS,
  isPermissionClaimAvailable,
  PERMISSION_CLAIM_GUARDS,
  PermissionClaims
} from 'access/claims';
import { isAccessible } from 'access/evaluate';

export type AccessLevel = 'none' | 'view' | 'full' | 'custom';

export const CUSTOM_TEMPLATE_VALUE = 'custom';

export type SegmentedOption = { value: AccessLevel; label: string };

export type Cell = {
  key: string;
  label: string;
  claimValue: string;
  explicitlyChecked: boolean;
  impliedRead: boolean;
  checked: boolean;
  isGlobalCell: boolean;
  locked: boolean;
  lockedRemovable: boolean;
};

export type StandalonePermission = {
  translationKey: string;
  fallbackLabel: string;
  value: string;
};

export type ModuleRow = {
  translationKey: string;
  fallbackLabel: string;
  prefix: string;
  opsMap: Map<string, string>;
};

const CRUD_OPS = new Set(['c', 'r', 'u', 'd']);

const HIDDEN_PERMISSION_CLAIMS = new Set<string>([
  PermissionClaims.TenantUserAccessCrud,
  PermissionClaims.GlobalClaimsAssign,
  PermissionClaims.CampaignMemberUpdate, // used only for patch endpoint, remove from here when full CM claims come
  PermissionClaims.ReminderCreate // used only for an endpoint, remove from here when full reminder claims come
]);

const USER_ACCESS_MANAGEMENT_TRIGGERS = new Set<string>([
  PermissionClaims.UserCreate,
  PermissionClaims.UserDelete
]);

const MODULE_ICONS: Record<string, IconType> = {
  tenant: IconType.account,
  user: IconType.users,
  apiKey: IconType.key,
  bl: IconType.link,
  sender: IconType.senders,
  order: IconType.orderandusage,
  child_tenant: IconType.companies,
  gl_tul: IconType.reports,
  response: IconType.comment,
  aifr: IconType.ai,
  aisr: IconType.aichat
};

const STANDALONE_ICONS: Record<string, IconType> = {
  [PermissionClaims.TenantClaimsAssign]: IconType.userlock,
  [PermissionClaims.AiSupportConfig]: IconType.aisettings,
  [PermissionClaims.ProviderManage]: IconType.providers,
  [PermissionClaims.FeatureFlagManage]: IconType.flag,
  [PermissionClaims.OrderTypeManage]: IconType.orderandusage
};

export const moduleIconFor = (modulePrefix: string): IconType =>
  MODULE_ICONS[modulePrefix] ?? IconType.customize;

export const standaloneIconFor = (claimValue: string): IconType =>
  STANDALONE_ICONS[claimValue] ?? IconType.customize;

const formatModuleLabel = (prefix: string): string =>
  prefix
    .replaceAll('_', ' ')
    .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
    .replaceAll(/\b\w/g, (ch) => ch.toUpperCase());

const toTranslationKey = (raw: string): string =>
  raw.replaceAll(/[._]([a-zA-Z])/g, (_, ch) => ch.toUpperCase());

const handleCrudOrStandaloneClaim = (
  moduleMap: Map<string, Map<string, string>>,
  standaloneList: StandalonePermission[],
  claimValue: string,
  prefix: string,
  op: string
): void => {
  if (CRUD_OPS.has(op)) {
    if (!moduleMap.has(prefix)) {
      moduleMap.set(prefix, new Map());
    }
    moduleMap.get(prefix)!.set(op, claimValue);
    return;
  }
  if (op === 'crud') {
    if (!moduleMap.has(prefix)) {
      moduleMap.set(prefix, new Map());
    }
    const m = moduleMap.get(prefix)!;
    for (const k of CRUD_OPS) {
      m.set(k, claimValue);
    }
    return;
  }
  standaloneList.push({
    translationKey: toTranslationKey(claimValue),
    fallbackLabel: formatModuleLabel(`${prefix} ${op}`),
    value: claimValue
  });
};

const derivePermissionGroups = (): {
  moduleRows: ModuleRow[];
  standaloneList: StandalonePermission[];
} => {
  const moduleMap = new Map<string, Map<string, string>>();
  const standaloneList: StandalonePermission[] = [];
  for (const claimValue of Object.values(PermissionClaims)) {
    if (HIDDEN_PERMISSION_CLAIMS.has(claimValue)) {
      continue;
    }

    const dotIndex = claimValue.lastIndexOf('.');
    if (dotIndex === -1) {
      standaloneList.push({
        translationKey: toTranslationKey(claimValue),
        fallbackLabel: formatModuleLabel(claimValue),
        value: claimValue
      });
      continue;
    }
    const prefix = claimValue.substring(0, dotIndex);
    const op = claimValue.substring(dotIndex + 1);
    handleCrudOrStandaloneClaim(moduleMap, standaloneList, claimValue, prefix, op);
  }
  const moduleRows = Array.from(moduleMap.entries()).map(([prefix, opsMap]) => ({
    translationKey: toTranslationKey(prefix),
    fallbackLabel: formatModuleLabel(prefix),
    prefix,
    opsMap
  }));
  return { moduleRows, standaloneList };
};

export const { moduleRows, standaloneList } = derivePermissionGroups();

const translatorEntry = (translator: ITranslator, key: string): unknown =>
  (translator as unknown as Record<string, unknown>)[key];

export const resolveLabel = (
  translator: ITranslator,
  translationKey: string,
  fallback: string,
  logMissing = true
): string => {
  const translationKeyCombined = 'permissionLabel_' + translationKey;
  const fn = translatorEntry(translator, translationKeyCombined);

  if (typeof fn === 'function') {
    return (fn as () => string)();
  }

  if (logMissing) {
    console.error('Missing translation for key:', translationKeyCombined);
  }
  return fallback;
};

export const resolveDescription = (translator: ITranslator, translationKey: string): string => {
  const fn = translatorEntry(translator, 'permissionDescription_' + translationKey);
  if (typeof fn === 'function') {
    return (fn as () => string)();
  }
  return '';
};

export const isGlobalClaimValue = (claimValue: string): boolean =>
  INTERNAL_PERMISSION_CLAIMS.has(claimValue);

export const holdsPermissionGuard = (claimValue: string, heldClaims: string[]): boolean => {
  const guard = PERMISSION_CLAIM_GUARDS[claimValue as PermissionClaims];
  return !guard || isAccessible([`permissions_${guard}`], heldClaims);
};

export const isPermissionLocked = (claimValue: string, capabilities: TenantCapabilities): boolean =>
  !isPermissionClaimAvailable(claimValue, capabilities);

export const isPermissionVisible = (
  claimValue: string,
  heldClaims: string[],
  capabilities: TenantCapabilities
): boolean =>
  holdsPermissionGuard(claimValue, heldClaims) &&
  isPermissionClaimAvailable(claimValue, capabilities);

export const hasVisiblePermissions = (
  heldClaims: string[],
  capabilities: TenantCapabilities
): boolean =>
  moduleRows.some((module) =>
    Array.from(module.opsMap.values()).some((claimValue) =>
      isPermissionVisible(claimValue, heldClaims, capabilities)
    )
  ) || standaloneList.some((perm) => isPermissionVisible(perm.value, heldClaims, capabilities));

export const permissionsMatchTemplateRole = (
  claims: Claim[],
  templateRole: string,
  capabilities: TenantCapabilities
): boolean => {
  const toExplicitlyAssignedValues = (list: Claim[]) =>
    new Set(
      list
        .filter(
          (claim) =>
            claim.claimType === 'permissions' && !HIDDEN_PERMISSION_CLAIMS.has(claim.claimValue)
        )
        .map((claim) => claim.claimValue)
    );
  const selectedValues = toExplicitlyAssignedValues(claims);
  const templateValues = toExplicitlyAssignedValues(
    getClaimsByTemplateRole(templateRole, capabilities)
  );
  return (
    selectedValues.size === templateValues.size &&
    Array.from(templateValues).every((claimValue) => selectedValues.has(claimValue))
  );
};

export const withImplicitPermissionClaims = (claims: Claim[]): Claim[] => {
  const visibleClaims = claims.filter(
    (c: Claim) => !(c.claimType === 'permissions' && HIDDEN_PERMISSION_CLAIMS.has(c.claimValue))
  );
  const permissionValues = new Set(
    visibleClaims
      .filter((c: Claim) => c.claimType === 'permissions')
      .map((c: Claim) => c.claimValue)
  );

  const shouldAllowAccountAccessManagement = Array.from(USER_ACCESS_MANAGEMENT_TRIGGERS).some(
    (claimValue) => permissionValues.has(claimValue)
  );

  if (!shouldAllowAccountAccessManagement) {
    return visibleClaims;
  }

  return [
    ...visibleClaims,
    { claimType: 'permissions', claimValue: PermissionClaims.TenantUserAccessCrud }
  ];
};
